import { COUNTRY_CODES, CountryDetails } from '../../../constants/countryCodes';

const DEFAULT_REGEX = /^\+\d{10}$/;

// Match anything by default
const DEFAULT_COMPANY_REGISTRATION_REGEX = /^.+$/;

export type ISO = (typeof COUNTRY_CODES)[keyof typeof COUNTRY_CODES]['iso31661'];
export type ISOUpperCase = Uppercase<ISO>;

export type CountryType = {
  code: string;
  iso31661: ISO;
  name: string;
  flag: string;
  pattern: string;
  regex: RegExp;
  companyRegistrationRegex: RegExp[];
};

export type PhoneType = {
  countryCode: string;
  input: string;
  number: string;
};

export type CountryOptionType = {
  value: string;
  label: string;
  country: CountryType;
};

export const toCountry = (countryCode: string, countryDetails: CountryDetails): CountryType => {
  const {
    flag,
    iso31661,
    name,
    pattern = '',
    regex = DEFAULT_REGEX,
    companyRegistrationRegex = [DEFAULT_COMPANY_REGISTRATION_REGEX],
  } = countryDetails;
  return { code: countryCode, flag, iso31661: iso31661 as ISO, name, pattern, regex, companyRegistrationRegex };
};

export const getCountries = (): CountryType[] => {
  return Object.entries(COUNTRY_CODES).map(([countryCode, countryDetails]) => toCountry(countryCode, countryDetails));
};

export const numberToPhoneType = (number: string): PhoneType => {
  const countryCodeLengths = [3, 2, 1];

  const phone = countryCodeLengths
    .map((countryCodeLength) => {
      const countryCode = getCountries().find(({ code }) => code === number.substr(1, countryCodeLength));
      if (countryCode)
        return {
          countryCode: countryCode.code,
          input: number.substr(countryCodeLength + 1),
          number,
        };

      return null;
    })
    .find((phone) => !!phone);

  if (phone) return phone;

  return { countryCode: '', input: '', number: '' };
};

export const toCountryOption = (country: CountryType): CountryOptionType => ({
  value: country.code,
  label: `${country.flag} (+${country.code}) ${country.name}`,
  country,
});

export const phoneCountriesOptions = getCountries().map(toCountryOption);

export const getDefaultCountry = (countryCode: string) => {
  const countries = getCountries();
  return countries.find(({ code }) => code === countryCode) || countries[0];
};

export const phoneTypeToNumber = ({ countryCode, number }: { countryCode: string; number: string }) => {
  if (!countryCode || !number) return '';
  // remove special characters
  const cleanNumber = number.replace(/\D/g, '');
  return `+${countryCode}${cleanNumber}`;
};

export const getPhoneCodeByIso = (country: string) => {
  const countries = getCountries();
  return countries.find(({ iso31661 }) => iso31661 === country)?.code || countries[0].code;
};

export const extractPhoneIsoCode = (phone: string): string | undefined => {
  const phoneType = numberToPhoneType(phone);
  const country = COUNTRY_CODES[phoneType.countryCode as keyof typeof COUNTRY_CODES];

  return country?.iso31661;
};
